import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import jwtService from 'app/services/jwtService';

const SUCCESS_MESSAGE = 'Your account is ready. You can now sign in with your iLoan email address.';
const INVALID_LINK_MESSAGE =
  'This invitation link is invalid or has expired. Ask an administrator to send a new one.';

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#f5f5f5',
    padding: '20px'
  },
  formBox: {
    width: '100%',
    maxWidth: '600px',
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    '@media screen and (max-width: 500px)': {
      padding: '15px'
    }
  },
  title: {
    fontFamily: 'Arial, sans-serif',
    marginTop: 0,
    marginBottom: '8px'
  },
  intro: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    marginTop: 0,
    marginBottom: '24px',
    color: 'rgba(0,0,0,0.6)'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px'
  },
  successMessage: {
    marginTop: '15px',
    color: 'green',
    textAlign: 'center',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px'
  },
  errorMessage: {
    marginTop: '15px',
    color: '#cf1e2b',
    textAlign: 'center',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px'
  },
  backLink: {
    display: 'block',
    marginTop: '16px',
    textAlign: 'center',
    color: '#2f2f2f !important',
    fontSize: '14px !important',
    fontWeight: '600 !important',
    textTransform: 'capitalize !important',

    '&:hover': {
      color: '#2f2f2f !important'
    }
  }
});

function AcceptInvitation(props) {
  const classes = useStyles();
  const { token } = props.match.params;
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      await jwtService.acceptInvitation(token, password, passwordConfirmation);
      setSuccess(true);
    } catch (requestError) {
      setError(INVALID_LINK_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={classes.container}>
      <form className={classes.formBox} onSubmit={handleSubmit}>
        <h1 className={classes.title}>Welcome to iLoan Central</h1>
        <p className={classes.intro}>Choose a password to activate your account.</p>

        <TextField
          fullWidth
          required
          className="mt-8 mb-16"
          id="password"
          name="password"
          label="Password"
          variant="outlined"
          type="password"
          value={password}
          disabled={success}
          onChange={event => setPassword(event.target.value)}
        />

        <TextField
          fullWidth
          required
          className="mt-8 mb-16"
          id="passwordConfirmation"
          name="passwordConfirmation"
          label="Confirm password"
          variant="outlined"
          type="password"
          value={passwordConfirmation}
          disabled={success}
          onChange={event => setPasswordConfirmation(event.target.value)}
        />

        <div className={classes.buttonContainer}>
          <Button
            type="submit"
            disabled={submitting || success}
            className="whitespace-no-wrap normal-case"
            variant="contained"
            color="secondary"
          >
            Activate my account
          </Button>
        </div>

        {success && <p className={classes.successMessage}>{SUCCESS_MESSAGE}</p>}
        {error && <p className={classes.errorMessage}>{error}</p>}

        <Link className={classes.backLink} to="/login">
          Go to sign in
        </Link>
      </form>
    </div>
  );
}

export default AcceptInvitation;
